import ClientService from '@boServices/clientService';
import CollectionService from '@boServices/collectionService';
import { AlgoliaFacets, AlgoliaIndexSwitch, AlgoliaPagination, AlgoliaSearchBar, AlgoliaTestHits } from '@sharedComponents/Algolia';
import FlexBox from '@sharedComponents/FlexBox';
import Loader from '@sharedComponents/Loader';
import Select from '@sharedComponents/Select';
import Spacer from '@sharedComponents/Spacer';
import TestSheet from '@sharedComponents/TestSheet';
import Typography from '@sharedComponents/Typography';
import { useModal } from '@sharedContexts/ModalContext';
import { liteClient } from 'algoliasearch/lite';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Configure, InstantSearch } from 'react-instantsearch';

import * as style from './style.module.css';

const clientService = new ClientService();
const collectionService = new CollectionService();

const PAGE_SIZE = 16;
const CLIENT_SEARCH_DEBOUNCE_MS = 300;
const CLIENT_SEARCH_LIMIT = 20;

/**
 * Fetches the Algolia search access scoped to a client reference, and refetches it
 * whenever the reference changes. An empty reference means the public collections.
 */
const useAlgoliaSearchAccess = (clientRef) => {
    const [state, setState] = useState({ access: null, error: false });

    useEffect(() => {
        let cancelled = false;
        setState({ access: null, error: false });
        collectionService
            .getAlgoliaSearchKey(clientRef)
            .then((access) => !cancelled && setState({ access, error: false }))
            .catch(() => !cancelled && setState({ access: null, error: true }));

        return () => {
            cancelled = true;
        };
    }, [clientRef]);

    return state;
};

/**
 * Searches the clients by name and keeps the picked one in the options, so the select
 * can still display it once a later search no longer returns it.
 */
const useClientOptions = (publicOption, picked) => {
    const [search, setSearch] = useState('');
    const [fetched, setFetched] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            clientService
                .getClients({ search, perPage: CLIENT_SEARCH_LIMIT })
                .then(({ items }) => {
                    if (cancelled) return;
                    setFetched(
                        items
                            .filter((client) => client.reference)
                            .map((client) => ({ value: client.reference, label: `${client.name} (${client.reference})` })),
                    );
                })
                .catch(() => !cancelled && setFetched([]));
        }, CLIENT_SEARCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search]);

    const options = useMemo(() => {
        const rest = fetched.filter((option) => option.value !== picked?.value);
        return [publicOption, ...(picked ? [picked] : []), ...rest];
    }, [fetched, picked, publicOption]);

    return { options, setSearch };
};

/**
 * Algolia grid of the collections: facets on the left, search, results and pagination on
 * the right. Read-only, like the one the candidates see.
 */
const AlgoliaGrid = ({ access, onView }) => {
    const { t } = useTranslation();
    const resultsRef = useRef(null);
    const searchClient = useMemo(() => {
        const client = liteClient(access.appId, access.key);

        // The preview must reflect a publication made a moment ago, possibly from another tab:
        // every search starts from an empty cache instead of replaying a stale response.
        return {
            ...client,
            search: async (requests) => {
                await client.clearCache();
                return client.search(requests);
            },
        };
    }, [access.appId, access.key]);
    const indexes = useMemo(() => ({ relevance: access.indexName, alphabetical: access.indexName }), [access.indexName]);

    return (
        <InstantSearch key={access.key} searchClient={searchClient} indexName={access.indexName}>
            <Configure hitsPerPage={PAGE_SIZE} />
            <AlgoliaIndexSwitch indexes={indexes} />
            <FlexBox gap="l" alignItems="flex-start">
                <div className={style.facets}>
                    <AlgoliaFacets t={t} />
                </div>
                <div ref={resultsRef} className={style.results}>
                    <AlgoliaSearchBar indexes={indexes} />
                    <div className={style.hits}>
                        <AlgoliaTestHits openTestDetails={onView} readOnly />
                    </div>
                    <Spacer y="m" />
                    <FlexBox justifyContent="flex-end">
                        <AlgoliaPagination scrollTargetRef={resultsRef} />
                    </FlexBox>
                </div>
            </FlexBox>
        </InstantSearch>
    );
};

AlgoliaGrid.propTypes = {
    access: PropTypes.shape({ appId: PropTypes.string, indexName: PropTypes.string, key: PropTypes.string }).isRequired,
    onView: PropTypes.func.isRequired,
};

/**
 * Preview of the collections as the Algolia-backed booking page shows them, for the public
 * collections or for one client.
 */
const CollectionsPreview = () => {
    const { t } = useTranslation('bo');
    const { actions: modalActions } = useModal();

    const publicOption = useMemo(() => ({ value: '', label: t('Public collections') }), [t]);
    const [picked, setPicked] = useState(null);
    const clientRef = picked?.value ?? '';
    const { options, setSearch } = useClientOptions(publicOption, picked);
    const { access, error } = useAlgoliaSearchAccess(clientRef);

    const openCollectionDetails = (collection) => {
        modalActions.open({
            size: 'l',
            component: () => <TestSheet test={collection} onClose={() => modalActions.close()} readOnly />,
            isClosable: true,
        });
    };

    return (
        <FlexBox flexDirection="column" gap="m">
            <FlexBox alignItems="center" gap="s">
                <Typography fontWeight="bold">{t('Client')}</Typography>
                <div className={style.picker}>
                    <Select
                        options={options}
                        value={clientRef}
                        filterOption={null}
                        onInputChange={(value, { action }) => action === 'input-change' && setSearch(value)}
                        onChange={(option) => setPicked(option?.value ? option : null)}
                    />
                </div>
            </FlexBox>

            {error && <Typography color="error-primary">{t('The collections preview could not be loaded.')}</Typography>}
            {!error && !access && <Loader />}
            {access && <AlgoliaGrid access={access} onView={openCollectionDetails} />}
        </FlexBox>
    );
};

export default CollectionsPreview;
